// src/HomeRoutes.jsx
import React from 'react';
import { createBrowserRouter, redirect } from 'react-router-dom';

import { RequireAuth, RequireRole } from './context/AuthContext';
import PublicLayout from './layouts/PublicLayout';
import PrivateLayout from './layouts/PrivateLayout';
import PublicOnly from './layouts/PublicOnly';

import Home from './pages/public/Home';
import ServiceInfo from './pages/public/ServiceInfo';
import Pricing from './pages/public/Pricing';
import Support from './pages/public/Support';
import Login from './pages/public/Login';
import Signup from './pages/public/Signup';

import Dashboard from './pages/Dashboard';
import Simulation, { loader as simulationLoader } from './pages/Simulation';

import MyPage from './pages/MyPage';
import Settings from './pages/Settings';
import Managers from './pages/UserManagement';

import ServiceAdmin, { loader as serviceListLoader } from './pages/system/ServiceAdmin';
import ServiceApply, { loader as serviceApplyLoader } from './pages/system/ServiceApply';
import ServiceBoard, { loader as serviceBoardLoader } from './pages/system/ServiceBoard';

import { requireUser } from './context/Sessions';

// 👉 고객지원 상세 페이지 (사용자용)
import NoticeList from './pages/support/NoticeList';
import NoticeDetail from './pages/support/NoticeDetail';
import FaqPage from './pages/support/FaqPage';
import InquiryPage from './pages/support/InquiryPage';

// 👉 고객지원 관리자 페이지 (SYSTEM 전용)
import AdminNoticePage from './pages/admin/AdminNoticePage';
import AdminFaqPage from './pages/admin/AdminFaqPage';
import AdminInquiryPage from './pages/admin/AdminInquiryPage';

const NotFound = () => <div style={{ padding: 24 }}>페이지를 찾을 수 없습니다.</div>;

// ✅ Router 객체를 만들어 반환
export function createAppRouter() {
    return createBrowserRouter([
        {
            path: '/',
            element: <PublicLayout />,
            errorElement: <NotFound />,
            children: [
                {
                    index: true,
                    element: (
                        <PublicOnly>
                            <Home />
                        </PublicOnly>
                    ),
                },
                {
                    path: 'login',
                    element: (
                        <PublicOnly>
                            <Login />
                        </PublicOnly>
                    ),
                },
                {
                    path: 'signup',
                    element: (
                        <PublicOnly>
                            <Signup />
                        </PublicOnly>
                    ),
                },

                // 공개 페이지
                { path: 'service', element: <ServiceInfo /> },
                { path: 'pricing', element: <Pricing /> },
                { path: 'support', element: <Support /> },

                // 🔹 고객지원 - 공지/FAQ는 로그인 없이 조회 가능
                { path: 'notices', element: <NoticeList /> },
                { path: 'support/notices/:id', element: <NoticeDetail /> },
                { path: 'support/faqs', element: <FaqPage /> },
                { path: 'support/inquiries', element: <InquiryPage /> },
            ],
        },

        // 보호 라우트
        {
            element: (
                <RequireAuth>
                    <PrivateLayout />
                </RequireAuth>
            ),
            id: 'private', // ← 고유 id
            loader: async ({ request }) => {
                const user = await requireUser(request);
                return { user };
            },
            errorElement: <NotFound />,
            children: [
                { path: 'dashboard', element: <Dashboard /> },

                // USER, PERSONAL 전용
                {
                    // 시뮬레이션 페이지
                    path: 'simulation',
                    element: (
                        <RequireRole allow={['USER', 'PERSONAL']}>
                            <Simulation />
                        </RequireRole>
                    ),
                    loader: simulationLoader, // ✅ 페이지 진입 시 WS 선연결
                },
                {
                    // 서비스 선택 페이지
                    path: 'service-board',
                    element: (
                        <RequireRole allow={['USER', 'PERSONAL']}>
                            <ServiceBoard />
                        </RequireRole>
                    ),
                    loader: serviceBoardLoader,
                },
                {
                    // 서비스 신청 페이지
                    path: 'services-apply',
                    element: (
                        <RequireRole allow={['ADMIN', 'PERSONAL']}>
                            <ServiceApply />
                        </RequireRole>
                    ),
                    loader: serviceApplyLoader,
                },

                // 🔹 1:1 문의 페이지 (로그인 필수)
                // {
                //     path: "support/inquiries",
                //     element: (
                //         <RequireRole allow={["USER", "PERSONAL", "ADMIN"]}>
                //             <InquiryPage />
                //         </RequireRole>
                //     ),
                // },

                // ADMIN 전용
                {
                    path: 'settings',
                    element: (
                        <RequireRole allow="ADMIN">
                            <Settings />
                        </RequireRole>
                    ),
                },
                {
                    path: 'user-manager',
                    element: (
                        <RequireRole allow="ADMIN">
                            <Managers />
                        </RequireRole>
                    ),
                },

                // USER, PERSONAL, ADMIN 전용
                {
                    path: 'mypage',
                    element: (
                        <RequireRole allow={['USER', 'PERSONAL', 'ADMIN']}>
                            <MyPage />
                        </RequireRole>
                    ),
                },

                // SYSTEM 전용
                {
                    path: 'serviceadmin',
                    element: (
                        <RequireRole allow={'SYSTEM'}>
                            <ServiceAdmin />
                        </RequireRole>
                    ),
                    loader: serviceListLoader, // ✅ 목록은 먼저 로드
                    // 필요 시 뒤로가기 재검증 막기
                    // shouldRevalidate: () => false,
                },
                // SYSTEM 전용 고객지원 관리자 메뉴
                {
                    path: 'support/admin/notices',
                    element: (
                        <RequireRole allow={'SYSTEM'}>
                            <AdminNoticePage />
                        </RequireRole>
                    ),
                },
                {
                    path: 'support/admin/faqs',
                    element: (
                        <RequireRole allow={'SYSTEM'}>
                            <AdminFaqPage />
                        </RequireRole>
                    ),
                },
                {
                    path: 'support/admin/inquiries',
                    element: (
                        <RequireRole allow={'SYSTEM'}>
                            <AdminInquiryPage />
                        </RequireRole>
                    ),
                },
            ],
        },

        { path: '*', element: <NotFound /> },
    ]);
}
